import { apiClient as api } from '@/lib/api';
import { NOTIFICATION_CHANNELS, type NotificationChannelKey } from '@easyflow/shared';

// Push subscription state is the only client-side cache we keep — it mirrors
// whether the browser PushManager has an active subscription, used to gate
// "subscribe again" UI on the settings page. All notification *enable/disable*
// decisions are now driven by the unified backend preferences (per category)
// and the OS-level Notification permission, never by localStorage flags.
const PUSH_SUBSCRIPTION_KEY = 'edgvoip_push_subscription';
const NOTIFICATION_ICON = '/notifications/notification-icon-192.png';
const NOTIFICATION_BADGE = '/notifications/notification-badge-96.png';
const BRAND = 'EasyFlow';

/**
 * Canali di messaggistica con asset/label dedicati per le notifiche desktop.
 * Sottoinsieme di NotificationChannelKey: gli asset/label vivono nella SoT
 * condivisa `NOTIFICATION_CHANNELS`.
 */
export type MessageChannel = Extract<NotificationChannelKey, 'sms' | 'whatsapp' | 'telegram'>;

export interface NotificationOptions {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  tag?: string;
  data?: Record<string, unknown>;
  onClick?: () => void;
  requireInteraction?: boolean;
}

class NotificationService {
  private isSupported: boolean;
  private permissionStatus: NotificationPermission = 'default';
  private vapidPublicKey: string | null = null;
  private pushSubscription: PushSubscription | null = null;

  constructor() {
    this.isSupported = 'Notification' in window;
    if (this.isSupported) {
      this.permissionStatus = Notification.permission;
    }
  }

  get supported(): boolean {
    return this.isSupported;
  }

  get pushSupported(): boolean {
    return 'serviceWorker' in navigator && 'PushManager' in window;
  }

  get permission(): NotificationPermission {
    // Read the live browser permission every time instead of the value cached
    // once in the constructor (Task #2395). The user can grant permission after
    // the service was instantiated (settings tab, useDesktopNotifications flow,
    // or the OS prompt) — a stale cached `default` would keep `show()` silently
    // no-op until a full page reload. Resync the cache from the live value.
    if (this.isSupported) {
      this.permissionStatus = Notification.permission;
    }
    return this.permissionStatus;
  }

  get enabled(): boolean {
    // "Enabled" now reflects only what the platform allows: notifications are
    // supported AND the user granted browser permission. Per-category gating
    // happens server-side via /api/notifications/preferences and is enforced
    // by the push delivery pipeline. Uses the live `permission` getter so a
    // freshly-granted permission takes effect without a reload.
    return this.isSupported && this.permission === 'granted';
  }

  get isPushSubscribed(): boolean {
    return !!localStorage.getItem(PUSH_SUBSCRIPTION_KEY);
  }

  get isBlocked(): boolean {
    // The browser has denied (or auto-blocked after repeated dismissals) the
    // Notification permission. In this state `Notification.requestPermission()`
    // resolves to 'denied' without ever showing a prompt, so the UI must guide
    // the user to unblock it from the site settings instead of retrying.
    return this.isSupported && this.permission === 'denied';
  }

  async requestPermission(): Promise<NotificationPermission> {
    if (!this.isSupported) {
      console.warn('[NotificationService] Notifications not supported');
      return 'denied';
    }

    if (this.permissionStatus === 'granted') {
      return 'granted';
    }

    try {
      const permission = await Notification.requestPermission();
      this.permissionStatus = permission;
      return permission;
    } catch (error) {
      console.error('[NotificationService] Error requesting permission:', error);
      return 'denied';
    }
  }

  async initPushNotifications(): Promise<boolean> {
    if (!this.pushSupported) {
      console.warn('[NotificationService] Push notifications not supported');
      return false;
    }

    try {
      const response = await api.get<{ data: { vapidPublicKey: string } }>('/notifications/push/vapid-key');
      if (response.data?.data?.vapidPublicKey) {
        this.vapidPublicKey = response.data.data.vapidPublicKey;
        console.log('[NotificationService] VAPID key loaded');
        return true;
      }
    } catch (error) {
      console.warn('[NotificationService] Web Push not configured on server');
    }
    return false;
  }

  async subscribeToPush(): Promise<boolean> {
    if (!this.pushSupported || !this.vapidPublicKey) {
      console.warn('[NotificationService] Cannot subscribe: Push not supported or VAPID key missing');
      return false;
    }

    try {
      const permission = await this.requestPermission();
      if (permission !== 'granted') {
        console.warn('[NotificationService] Permission denied');
        return false;
      }

      const registration = await navigator.serviceWorker.ready;
      
      const existingSubscription = await registration.pushManager.getSubscription();
      if (existingSubscription) {
        this.pushSubscription = existingSubscription;
        await this.sendSubscriptionToServer(existingSubscription);
        localStorage.setItem(PUSH_SUBSCRIPTION_KEY, 'true');
        console.log('[NotificationService] Existing push subscription updated');
        return true;
      }

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(this.vapidPublicKey)
      });

      this.pushSubscription = subscription;
      await this.sendSubscriptionToServer(subscription);
      localStorage.setItem(PUSH_SUBSCRIPTION_KEY, 'true');
      console.log('[NotificationService] New push subscription created');
      return true;
    } catch (error) {
      console.error('[NotificationService] Error subscribing to push:', error);
      return false;
    }
  }

  async unsubscribeFromPush(): Promise<boolean> {
    if (!this.pushSupported) return false;

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await api.post('/notifications/push/unsubscribe', { endpoint: subscription.endpoint });
        await subscription.unsubscribe();
      }

      localStorage.removeItem(PUSH_SUBSCRIPTION_KEY);
      this.pushSubscription = null;
      console.log('[NotificationService] Unsubscribed from push');
      return true;
    } catch (error) {
      console.error('[NotificationService] Error unsubscribing from push:', error);
      return false;
    }
  }

  async checkPushStatus(): Promise<{ subscribed: boolean; deviceCount: number }> {
    try {
      const response = await api.get<{ data: { enabled: boolean; subscriptions: unknown[] } }>('/notifications/push/status');
      const data = response.data?.data;
      if (data) {
        return { 
          subscribed: data.subscriptions && data.subscriptions.length > 0,
          deviceCount: data.subscriptions?.length || 0
        };
      }
      return { subscribed: false, deviceCount: 0 };
    } catch (error) {
      console.warn('[NotificationService] Error checking push status:', error);
      return { subscribed: false, deviceCount: 0 };
    }
  }

  private async sendSubscriptionToServer(subscription: PushSubscription): Promise<void> {
    const subscriptionData = subscription.toJSON();
    await api.post('/notifications/push/subscribe', {
      subscription: {
        endpoint: subscriptionData.endpoint,
        keys: {
          p256dh: subscriptionData.keys?.p256dh,
          auth: subscriptionData.keys?.auth
        }
      }
    });
  }

  private urlBase64ToUint8Array(base64String: string): ArrayBuffer {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray.buffer;
  }

  show(options: NotificationOptions): Notification | null {
    if (!this.enabled) {
      console.log('[NotificationService] Notifications disabled, skipping');
      return null;
    }

    try {
      const requireInteraction = options.requireInteraction === true;
      const notification = new Notification(options.title, {
        body: options.body,
        icon: options.icon || NOTIFICATION_ICON,
        badge: options.badge || NOTIFICATION_BADGE,
        tag: options.tag,
        data: options.data,
        requireInteraction,
        silent: false
      });

      if (options.onClick) {
        // onClick è catturato nella closure di QUESTA notifica: con tag stabili
        // condivisi tra i due path desktop, un Map per-tag verrebbe svuotato
        // dall'evento `close` della notifica sostituita, perdendo l'handler.
        const onClick = options.onClick;
        notification.onclick = () => {
          window.focus();
          onClick();
          notification.close();
        };
      }

      if (!requireInteraction) {
        setTimeout(() => notification.close(), 10000);
      }

      return notification;
    } catch (error) {
      console.error('[NotificationService] Error showing notification:', error);
      return null;
    }
  }

  showCall(callerName: string, callerNumber: string, onAnswer?: () => void): Notification | null {
    return this.show({
      title: `${BRAND} - Chiamata in arrivo`,
      body: callerName !== callerNumber ? `${callerName}\n${callerNumber}` : callerNumber,
      icon: NOTIFICATION_ICON,
      tag: `call-${callerNumber}`,
      onClick: onAnswer
    });
  }

  showMessage(senderName: string, channel: MessageChannel, preview: string, messageId?: string, onClick?: () => void): Notification | null {
    const def = NOTIFICATION_CHANNELS[channel];
    return this.show({
      title: `${def.label} · ${senderName}`,
      body: preview.length > 120 ? preview.substring(0, 120) + '…' : preview,
      icon: def.icon,
      badge: def.badge,
      // Tag stabile per-messaggio: i due path desktop (messaging:new_message e
      // notification:new) collassano in un'unica popup invece di duplicarsi.
      tag: `msg-${messageId ?? senderName}`,
      onClick
    });
  }

  /**
   * Dispatch desktop generico ma canale-aware: usa icona/badge del canale
   * visivo (SoT `NOTIFICATION_CHANNELS`) invece dell'icona EasyFlow di default.
   * Usato dal NotificationHandler per email, calendario e Video Call Live.
   */
  showChannel(
    channelKey: NotificationChannelKey,
    title: string,
    body: string,
    opts?: { tag?: string; requireInteraction?: boolean; onClick?: () => void },
  ): Notification | null {
    const def = NOTIFICATION_CHANNELS[channelKey];
    return this.show({
      title,
      body: body.length > 160 ? body.substring(0, 160) + '…' : body,
      icon: def.icon,
      badge: def.badge,
      tag: opts?.tag,
      requireInteraction: opts?.requireInteraction,
      onClick: opts?.onClick,
    });
  }

  showMissedCall(callerName: string, callerNumber: string, contactBadge?: string, onClick?: () => void): Notification | null {
    const badge = contactBadge ? `${contactBadge} ` : '';
    return this.show({
      title: `${BRAND} - Chiamata persa`,
      body: `${badge}${callerName !== callerNumber ? `${callerName} (${callerNumber})` : callerNumber}`,
      icon: NOTIFICATION_ICON,
      tag: `missed-${callerNumber}-${Date.now()}`,
      onClick
    });
  }

  showVoicemail(callerName: string, duration: string, contactBadge?: string, onClick?: () => void): Notification | null {
    const badge = contactBadge ? `${contactBadge} ` : '';
    return this.show({
      title: `${BRAND} - Nuovo messaggio vocale`,
      body: `${badge}Da ${callerName} (${duration})`,
      icon: NOTIFICATION_ICON,
      tag: `voicemail-${Date.now()}`,
      onClick
    });
  }

  showLead(leadName: string, source: string, onClick?: () => void): Notification | null {
    return this.show({
      title: `${BRAND} - Nuovo Lead`,
      body: `${leadName}\nFonte: ${source}`,
      icon: NOTIFICATION_ICON,
      tag: `lead-${Date.now()}`,
      onClick
    });
  }

  showCallbackRequest(phone: string, name: string, agentName?: string, onClick?: () => void): Notification | null {
    const caller = name || phone;
    return this.show({
      title: `${BRAND} - Richiesta di richiamata`,
      body: `${caller}${name ? ` (${phone})` : ''}\nDa: ${agentName || 'Chatbot'}`,
      icon: NOTIFICATION_ICON,
      tag: `callback-${phone}-${Date.now()}`,
      onClick
    });
  }
}

export const notificationService = new NotificationService();
